import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Delete merchant danger zone (FIG-320 / REQ-038): apart from the groups,
// admin only, and the merchant's name must be typed to confirm. The deletion
// and its four terminal states are unchanged from the old Merchants list
// (UC-162 · AC-UC162-6, AC-UC162-10). Blocking delete while Deployed
// Consideration is outstanding needs UC-216.

// The success body and the Problem Details bodies the delete-organization
// function returns, reduced to the fields the outcome handling reads.
type DeletionOutcome = {
  success?: boolean;
  type?: string;
  status?: number;
  title?: string;
  detail?: string;
};

// A thrown FunctionsHttpError carries the original Response on `context`, which
// is the only place the edge function's Problem Details body survives.
async function readProblemDetails(error: unknown): Promise<DeletionOutcome | null> {
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response)) return null;
  try {
    return await context.clone().json();
  } catch {
    return null;
  }
}

interface DeleteMerchantDangerZoneProps {
  merchant: { id: string; name: string; status?: string | null };
}

export const DeleteMerchantDangerZone: React.FC<DeleteMerchantDangerZoneProps> = ({ merchant }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);

  const matches = typed.trim() === merchant.name.trim();

  const cancel = () => {
    setConfirming(false);
    setTyped('');
  };

  const reportOutcome = (data: DeletionOutcome) => {
    // Terminal State 1: Fully Deleted (200, success: true)
    if (data.success === true) {
      toast({ title: 'Organisation deleted', description: `${merchant.name} has been fully removed` });
      navigate('/admin');
      return;
    }

    // Terminal State 2: Incomplete (409) - the merchant is gone from the main database.
    if (data.type === '/errors/organisation/delete-incomplete' || data.status === 409) {
      console.warn('Organization deletion incomplete:', data);
      toast({
        title: 'Organisation partially deleted',
        description: data.detail ||
          `${merchant.name} core data removed, but some external resources remain. Contact support.`,
        variant: 'destructive',
      });
      navigate('/admin');
      return;
    }

    // Terminal State 3 & 4: Failed (500 delete-failed, 400 invalid-input) - stay on the merchant.
    if (data.type?.includes('/errors/organisation/') || (data.status ?? 0) >= 400) {
      console.error('Organization deletion failed:', data);
      toast({
        title: 'Deletion failed',
        description: data.detail || data.title || 'Organization could not be deleted',
        variant: 'destructive',
      });
      return;
    }

    console.error('Unexpected delete response format:', data);
    toast({
      title: 'Unexpected response',
      description: 'Deletion status unclear. Please refresh the page to verify.',
      variant: 'destructive',
    });
  };

  const confirmDelete = async () => {
    if (!matches) return;
    setDeleting(true);
    toast({ title: 'Deleting organisation', description: `Removing ${merchant.name} and all associated data...` });
    try {
      const { data } = await invokeFunction(supabase, 'delete-organization', {
        body: { organizationId: merchant.id, isDraft: false },
      });
      if (!data) {
        toast({ title: 'Error', description: 'No response from delete operation', variant: 'destructive' });
        return;
      }
      reportOutcome(data as DeletionOutcome);
    } catch (error) {
      // invokeFunction throws on non-503 errors, so states 2-4 arrive on the error.
      const problemDetails = await readProblemDetails(error);
      if (problemDetails) {
        reportOutcome(problemDetails);
        return;
      }
      console.error('Exception during organization deletion:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete organisation',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section aria-label="Delete merchant" className="rounded-lg border border-red-200 bg-red-50/40 p-5">
      <div className="flex flex-wrap items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-red-700" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-red-800">Delete merchant</div>
          <p className="text-sm text-muted-foreground">
            Permanently removes this merchant and its data. This can't be undone.
          </p>
        </div>
        {!confirming && (
          <Button type="button" variant="destructive" onClick={() => setConfirming(true)}>
            Delete merchant
          </Button>
        )}
      </div>
      {confirming && (
        <div className="mt-4 space-y-3">
          <Label htmlFor="delete-merchant-confirm">
            Type <span className="font-semibold">{merchant.name}</span> to confirm
          </Label>
          <Input
            id="delete-merchant-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            disabled={deleting}
          />
          <div className="flex gap-2">
            <Button type="button" variant="destructive" onClick={confirmDelete} disabled={!matches || deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete permanently
            </Button>
            <Button type="button" variant="outline" onClick={cancel} disabled={deleting}>Cancel</Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default DeleteMerchantDangerZone;
